import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { Archive, Loader2, ArrowLeft, Users, GraduationCap, Receipt, Search, Lock } from 'lucide-react';
import { formatDateTime } from '../../lib/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const YearArchive = () => {
    const { year } = useParams();
    const navigate = useNavigate();
    const [archives, setArchives] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('parents');
    const [q, setQ] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const r = await axios.get(`${API_URL}/api/year-archives/year/${encodeURIComponent(year)}`, { withCredentials: true });
                setArchives(r.data || []);
            } catch { toast.error('Failed to load archive'); }
            finally { setLoading(false); }
        })();
    }, [year]);

    // Flatten data
    const students = useMemo(() => {
        const list = [];
        for (const a of archives) {
            for (const s of (a.students || [])) {
                list.push({ ...s, _parent: a.parent_snapshot });
            }
        }
        return list;
    }, [archives]);

    const receipts = useMemo(() => {
        const list = [];
        for (const a of archives) {
            for (const r of (a.receipts || [])) {
                list.push({ ...r, _parent: a.parent_snapshot });
            }
        }
        list.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
        return list;
    }, [archives]);

    const totals = useMemo(() => {
        return archives.reduce((acc, a) => {
            acc.fees += a.totals?.fees || 0;
            acc.discount += a.totals?.discount || 0;
            acc.paid += a.totals?.paid || 0;
            acc.remaining += a.totals?.remaining || 0;
            return acc;
        }, { fees: 0, discount: 0, paid: 0, remaining: 0 });
    }, [archives]);

    const filterText = q.toLowerCase();
    const filteredParents = archives.filter(a => {
        const p = a.parent_snapshot || {};
        return `${p.first_name} ${p.last_name} ${p.email} ${p.reg_code}`.toLowerCase().includes(filterText);
    });
    const filteredStudents = students.filter(s =>
        `${s.first_name} ${s.last_name} ${s.reg_code} ${s._parent?.reg_code || ''}`.toLowerCase().includes(filterText)
    );
    const filteredReceipts = receipts.filter(r =>
        `${r.receipt_number} ${r._parent?.first_name} ${r._parent?.last_name} ${r._parent?.reg_code}`.toLowerCase().includes(filterText)
    );

    if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-[#064E3B]" /></div></DashboardLayout>;

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="year-archive-page">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}><ArrowLeft className="h-4 w-4 mr-1" />Back to Dashboard</Button>
                    <div>
                        <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit'] flex items-center gap-2">
                            <Archive className="h-6 w-6 text-[#7C3AED]" />Archive {year}
                            <Badge variant="outline" className="ml-2 text-xs"><Lock className="h-3 w-3 mr-1" />Read-only</Badge>
                        </h1>
                        <p className="text-sm text-[#4B5563]">Snapshot of the {year} school year — used only for historical records.</p>
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Stat label="Guardians" value={archives.length} />
                    <Stat label="Total Fees" value={`€${totals.fees.toFixed(2)}`} />
                    <Stat label="Total Paid" value={`€${totals.paid.toFixed(2)}`} color="text-[#10B981]" />
                    <Stat label="Outstanding" value={`€${totals.remaining.toFixed(2)}`} color={totals.remaining > 0 ? "text-[#F59E0B]" : "text-[#10B981]"} />
                </div>

                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4B5563]" />
                    <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name, code, receipt..." className="pl-9" data-testid="archive-search" />
                </div>

                <Tabs value={tab} onValueChange={setTab} className="space-y-4">
                    <TabsList>
                        <TabsTrigger value="parents" data-testid="tab-archive-parents"><Users className="h-4 w-4 mr-2" />Guardians ({archives.length})</TabsTrigger>
                        <TabsTrigger value="students" data-testid="tab-archive-students"><GraduationCap className="h-4 w-4 mr-2" />Students ({students.length})</TabsTrigger>
                        <TabsTrigger value="receipts" data-testid="tab-archive-receipts"><Receipt className="h-4 w-4 mr-2" />Receipts ({receipts.length})</TabsTrigger>
                    </TabsList>

                    <TabsContent value="parents">
                        <Card className="card-surface"><CardContent className="p-0">
                            {filteredParents.length ? (
                                <div className="overflow-x-auto"><Table>
                                    <TableHeader><TableRow>
                                        <TableHead>Reg #</TableHead>
                                        <TableHead>Guardian</TableHead>
                                        <TableHead>Email</TableHead>
                                        <TableHead>Mobile</TableHead>
                                        <TableHead>Students</TableHead>
                                        <TableHead className="text-right">Fees</TableHead>
                                        <TableHead className="text-right">Paid</TableHead>
                                        <TableHead className="text-right">Remaining</TableHead>
                                    </TableRow></TableHeader>
                                    <TableBody>
                                        {filteredParents.map(a => {
                                            const p = a.parent_snapshot || {};
                                            return (
                                                <TableRow key={a.id}>
                                                    <TableCell className="font-mono text-xs">{p.reg_code}</TableCell>
                                                    <TableCell className="font-medium">{p.first_name} {p.last_name}</TableCell>
                                                    <TableCell className="text-sm">{p.email}</TableCell>
                                                    <TableCell className="text-sm">{p.mobile}</TableCell>
                                                    <TableCell><Badge variant="secondary">{(a.students || []).length}</Badge></TableCell>
                                                    <TableCell className="text-right tabular-nums">€{(a.totals?.fees || 0).toFixed(2)}</TableCell>
                                                    <TableCell className="text-right tabular-nums text-[#10B981]">€{(a.totals?.paid || 0).toFixed(2)}</TableCell>
                                                    <TableCell className={`text-right tabular-nums ${(a.totals?.remaining||0)>0?'text-[#F59E0B]':'text-[#10B981]'}`}>€{(a.totals?.remaining || 0).toFixed(2)}</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table></div>
                            ) : <div className="p-6 text-center text-[#6B7280]">No guardians archived</div>}
                        </CardContent></Card>
                    </TabsContent>

                    <TabsContent value="students">
                        <Card className="card-surface"><CardContent className="p-0">
                            {filteredStudents.length ? (
                                <div className="overflow-x-auto"><Table>
                                    <TableHeader><TableRow>
                                        <TableHead>Reg #</TableHead>
                                        <TableHead>Name</TableHead>
                                        <TableHead>DOB</TableHead>
                                        <TableHead>Program</TableHead>
                                        <TableHead>Class</TableHead>
                                        <TableHead>Teacher</TableHead>
                                        <TableHead>Guardian</TableHead>
                                    </TableRow></TableHeader>
                                    <TableBody>
                                        {filteredStudents.map(s => (
                                            <TableRow key={s.id + (s._parent?.id || '')}>
                                                <TableCell className="font-mono text-xs">{s.reg_code}</TableCell>
                                                <TableCell>
                                                    <div className="font-medium">{s.first_name} {s.last_name}</div>
                                                    {(s.first_name_ar || s.last_name_ar) && <div className="text-xs text-[#4B5563]" dir="rtl">{s.first_name_ar} {s.last_name_ar}</div>}
                                                </TableCell>
                                                <TableCell className="tabular-nums text-sm">{s.date_of_birth}</TableCell>
                                                <TableCell className="text-sm">{s.program_name || '—'}</TableCell>
                                                <TableCell className="text-sm">{s.class_name || '—'}</TableCell>
                                                <TableCell className="text-sm">{s.teacher_name || '—'}</TableCell>
                                                <TableCell className="text-sm">{s._parent?.first_name} {s._parent?.last_name} <span className="text-xs text-[#6B7280]">({s._parent?.reg_code})</span></TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table></div>
                            ) : <div className="p-6 text-center text-[#6B7280]">No students archived</div>}
                        </CardContent></Card>
                    </TabsContent>

                    <TabsContent value="receipts">
                        <Card className="card-surface"><CardContent className="p-0">
                            {filteredReceipts.length ? (
                                <div className="overflow-x-auto"><Table>
                                    <TableHeader><TableRow>
                                        <TableHead>Receipt #</TableHead>
                                        <TableHead>Date</TableHead>
                                        <TableHead>Type</TableHead>
                                        <TableHead>Method</TableHead>
                                        <TableHead>Guardian</TableHead>
                                        <TableHead className="text-right">Amount</TableHead>
                                    </TableRow></TableHeader>
                                    <TableBody>
                                        {filteredReceipts.map(r => (
                                            <TableRow key={r.id || r.receipt_number}>
                                                <TableCell className="font-mono text-xs">{r.receipt_number}</TableCell>
                                                <TableCell className="text-xs tabular-nums">{formatDateTime(r.created_at)}</TableCell>
                                                <TableCell className="text-xs capitalize">{r.payment_type}</TableCell>
                                                <TableCell className="text-xs capitalize">{(r.method || '').replace('_',' ')}</TableCell>
                                                <TableCell className="text-sm">{r._parent?.first_name} {r._parent?.last_name} <span className="text-xs text-[#6B7280]">({r._parent?.reg_code})</span></TableCell>
                                                <TableCell className="text-right tabular-nums">€{(r.amount || 0).toFixed(2)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table></div>
                            ) : <div className="p-6 text-center text-[#6B7280]">No receipts archived</div>}
                        </CardContent></Card>
                    </TabsContent>
                </Tabs>
            </div>
        </DashboardLayout>
    );
};

const Stat = ({ label, value, color }) => (
    <Card className="card-surface"><CardContent className="p-4">
        <p className="text-xs text-[#6B7280]">{label}</p>
        <p className={`text-xl font-bold tabular-nums font-['Outfit'] ${color || 'text-[#1F2937]'}`}>{value}</p>
    </CardContent></Card>
);

export default YearArchive;
